import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { message, Spin, Button, Card } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import { theme } from '../../theme';

// Interfaces para los datos
interface Localidad {
  id: number;
  descripcion: string;
}

interface TipoResponsable {
  id: number;
  descripcion: string;
}

interface TipoDocumento {
  id: number;
  descripcion: string;
}

interface Actividad {
  id: number;
  descripcion: string;
}

interface Empresa {
  id: number;
  denominacion: string;
  direccion: string;
  mail: string;
  telefono: string;
  localidad: Localidad;
  civa: TipoResponsable;
  tdoc: TipoDocumento;
  actividad: Actividad;
}

interface Cliente {
  id: number;
  denominacion: string;
  ndocumento: string;
  direccion?: string;
}

interface Productor {
  id: number;
  denominacion: string;
  ndocumento: string;
  direccion?: string;
}

interface Producto {
  id: number;
  descripcion: string;
  tipo: number;
}

interface Ciclo {
  id: number;
  descripcion: string;
}

interface Establecimiento {
  id: number;
  descripcion: string;
}

interface Nlote {
  id: number;
  descripcion: string;
}

interface TicketBalanza {
  id: number;
  numero_ticket: number;
  numero_produccion: number;
  kilos_primera: number;
  kilos_segunda: number;
  kilos_netos: number;
  fecha_ticket: string;
  compra_servicio: string;
  cierre: string;
  rollo_identificacion?: string;
  numero_remito?: string;
  dtv?: string;
  cliente: Cliente;
  productor: Productor;
  producto: Producto;
  establecimiento: Establecimiento;
  nlote: Nlote;
}

interface RecepcionDespacho {
  id: number;
  descripcion: string;
}

interface Origen {
  id: number;
  denominacion: string;
  ndocumento: string;
}

interface Fardo {
  id: number;
  nro_fardo: number;
  kilos_fardo: number;
  kilos_tara: number;
  fecha_hora: string;
  id_sucursal: number;
  alta: number;
  cliente: Cliente;
  ciclo: Ciclo;
  producto: Producto;
  recepcion_despacho: RecepcionDespacho;
  origen: Origen;
}

interface Produccion {
  id: number;
  numero_produccion: number;
  fecha: string;
  fecha_hora_inicio: string;
  fecha_hora_final: string;
  demora_minutos: number;
  kilos_semilla: number;
  id_sucursal: number;
  alta: number;
  tipo_registracion: string;
  resto_positivo?: number;
  resto_negativo?: number;
  cliente: Cliente;
  productor: Productor;
  producto: Producto;
  ciclo: Ciclo;
}

interface TicketsData {
  data: TicketBalanza[];
  total_kilos_netos: number;
  cantidad_tickets: number;
}

interface FardosData {
  data: Fardo[];
  total_cantidad_fardos: number;
  total_peso_fardos: number;
}

interface Resumen {
  numero_produccion: number;
  fecha: string;
  cliente: string;
  productor: string;
  producto: string;
  total_kilos_netos: number;
  total_cantidad_fardos: number;
  total_peso_fardos: number;
  demora_minutos: number;
}

interface ProduccionData {
  empresa: Empresa;
  produccion: Produccion;
  tickets: TicketsData;
  fardos: FardosData;
  resumen: Resumen;
}

const EmisionProduccion: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ProduccionData | null>(null);
  const [error, setError] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  // Obtener parámetros de la URL
  const id_empresa = searchParams.get('id_empresa');
  const id_sucursal=searchParams.get('id_sucursal')
  const id_produccion = searchParams.get('id_produccion');

  useEffect(() => {
    const fetchData = async () => {
      if (!id_empresa || !id_produccion) {
        setError('Faltan parámetros requeridos: id_empresa e id_produccion');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        
        const response = await fetch(
          `${process.env.REACT_APP_PRODUCCION_URL}/busqueda_produccion/${id_empresa}/${id_sucursal}/${id_produccion}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        
        if (result.status === 'success') {
          setData(result.data);
          message.success(result.message);
        } else {
          setError(result.message || 'Error al obtener datos de producción');
        }
      } catch (error) {
        console.error('Error al obtener datos:', error);
        setError('Error de conexión al obtener datos de producción');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, id_empresa, id_produccion]);

  // Calcular rindes dinámicos según el producto
  const calcularRindes = () => {
    if (!data) return { rindeProducto: 0, rindeSemilla: 0 };

    const totalKilosNetos = data.tickets.total_kilos_netos;
    const kilosSemilla = data.produccion.kilos_semilla;
    const restoPositivo = data.produccion.resto_positivo || 0;
    const restoNegativo = data.produccion.resto_negativo || 0;
    const producto = data.produccion.producto?.descripcion || 'Producto';

    // Calcular sumatoria del peso neto de todos los fardos
    const sumatoriaPesoNeto = data.fardos.data.reduce((sum, fardo) => {
      return sum + ((fardo.kilos_fardo || 0) - (fardo.kilos_tara || 0));
    }, 0);

    // Rinde del producto = ((sumatoria peso neto + resto positivo - resto negativo) / kilos procesados) * 100
    const pesoTotalConRestos = sumatoriaPesoNeto + restoPositivo - restoNegativo;
    const rindeProducto = totalKilosNetos > 0 ? (pesoTotalConRestos / totalKilosNetos) * 100 : 0;
    
    // Rinde de semilla = (kilos semilla / kilos netos) * 100
    const rindeSemilla = totalKilosNetos > 0 ? (kilosSemilla / totalKilosNetos) * 100 : 0;

    return {
      rindeProducto: parseFloat(rindeProducto.toFixed(2)),
      rindeSemilla: parseFloat(rindeSemilla.toFixed(2)),
      nombreProducto: producto,
      pesoTotalConRestos: parseFloat(pesoTotalConRestos.toFixed(2)),
      sumatoriaPesoNeto: parseFloat(sumatoriaPesoNeto.toFixed(2))
    };
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        padding: '24px'
      }}>
        <Spin size="large" />
        <span style={{ marginLeft: '10px', color: theme.colors.textPrimary }}>Cargando datos de producción...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center' 
      }}>
        <h2 style={{ color: theme.colors.error }}>Error</h2>
        <p style={{ color: theme.colors.textSecondary }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center' 
      }}>
        <h2 style={{ color: theme.colors.textPrimary }}>No se encontraron datos</h2>
      </div>
    );
  }

  const rindes = calcularRindes();

  // Función para exportar a PDF usando jsPDF directamente
  const exportToPDF = () => {
    if (!data) return;
    
    try {
      setExporting(true);
      message.loading('Generando PDF...', 0);
      
      const doc = new jsPDF();
      let yPosition = 20;
      const lineHeight = 7;
      const pageWidth = doc.internal.pageSize.width;
      const leftMargin = 14;
      const rightMargin = pageWidth - 14;

      // Título principal
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const titleText = `EMISIÓN DE PRODUCCIÓN - N° ${data.resumen.numero_produccion}`;
      doc.text(titleText, pageWidth/2, yPosition, { align: 'center' });
      yPosition += lineHeight * 2;

      // Datos de la empresa
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('DATOS DE LA EMPRESA', leftMargin, yPosition);
      yPosition += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Denominación: ${data.empresa.denominacion}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Dirección: ${data.empresa.direccion}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Email: ${data.empresa.mail}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Teléfono: ${data.empresa.telefono}`, leftMargin, yPosition);
      yPosition += lineHeight * 2;

      // Datos de la producción
      doc.setFont('helvetica', 'bold');
      doc.text('DATOS DE LA PRODUCCIÓN', leftMargin, yPosition);
      yPosition += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Número: ${data.produccion.numero_produccion}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Fecha: ${data.produccion.fecha}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Cliente: ${data.produccion.cliente?.denominacion || 'N/A'}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Productor: ${data.produccion.productor?.denominacion || 'N/A'}`, leftMargin, yPosition);
      yPosition += lineHeight;
      doc.text(`Producto: ${data.produccion.producto?.descripcion || 'N/A'}`, leftMargin, yPosition);
      yPosition += lineHeight;
      
      // Tipo de Registración con color celeste
      const tipoRegistracion = data.produccion.tipo_registracion === 'M' ? 'MANUAL' : data.produccion.tipo_registracion === 'A' ? 'AUTOMÁTICA' : data.produccion.tipo_registracion || 'N/A';
      doc.setTextColor(135, 206, 235); // Color celeste (#87CEEB)
      doc.setFont('helvetica', 'bold');
             doc.text(`Tipo de Registración: ${tipoRegistracion}`, leftMargin, yPosition);
       doc.setTextColor(0, 0, 0); // Volver al color negro
       doc.setFont('helvetica', 'normal');
       yPosition += lineHeight;
       doc.text(`Resto (+): ${(data.produccion.resto_positivo || 0).toFixed(2)} kg`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Resto (-): ${(data.produccion.resto_negativo || 0).toFixed(2)} kg`, leftMargin, yPosition);
       yPosition += lineHeight * 2;

      // Resumen de rindes
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMEN DE RINDES', leftMargin, yPosition);
      yPosition += lineHeight;
      
      doc.setFont('helvetica', 'normal');
             doc.text(`Rinde ${rindes.nombreProducto}: ${rindes.rindeProducto}%`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Rinde Semilla: ${rindes.rindeSemilla}%`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Total Procesado: ${(data.tickets.total_kilos_netos || 0).toFixed(2)} kg`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Total Fardos: ${data.fardos.total_cantidad_fardos}`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Sumatoria Peso Neto: ${rindes.sumatoriaPesoNeto} kg`, leftMargin, yPosition);
       yPosition += lineHeight;
       doc.text(`Peso Total con Restos: ${rindes.pesoTotalConRestos} kg`, leftMargin, yPosition);
      yPosition += lineHeight * 2;

      // Tickets de balanza
      doc.setFont('helvetica', 'bold');
      doc.text(`TICKETS DE BALANZA (${data.tickets.cantidad_tickets})`, leftMargin, yPosition);
      yPosition += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      data.tickets.data.forEach((ticket, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.text(`Ticket ${index + 1}: ${ticket.numero_ticket} - ${(ticket.kilos_netos || 0).toFixed(2)} kg${ticket.numero_remito ? ` | Remito: ${ticket.numero_remito}` : ''}${ticket.dtv ? ` | DTV: ${ticket.dtv}` : ''}`, leftMargin, yPosition);
        yPosition += lineHeight;
      });
      yPosition += lineHeight;

      // Fardos
      doc.setFont('helvetica', 'bold');
      doc.text(`DETALLE DE FARDOS (${data.fardos.total_cantidad_fardos})`, leftMargin, yPosition);
      yPosition += lineHeight;
      
      doc.setFont('helvetica', 'normal');
      data.fardos.data.forEach((fardo, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.text(`Fardo ${index + 1}: ${fardo.nro_fardo} - ${(fardo.kilos_fardo || 0).toFixed(2)} kg`, leftMargin, yPosition);
        yPosition += lineHeight;
      });

      const fileName = `Emision_Produccion_${data.resumen.numero_produccion}_${data.resumen.fecha}.pdf`;
      doc.save(fileName);
      
      message.destroy();
      message.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.destroy();
      message.error('Error al generar PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Emisión de Producción - N° {data.resumen.numero_produccion}
      </h1>

      {/* Botón Exportar PDF */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '24px'
      }}>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportToPDF}
          loading={exporting}
          size="large"
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500
          }}
        >
          Exportar a PDF
        </Button>
      </div>

      {/* Contenido */}
      <div>

      {/* Datos de la Empresa */}
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
        title="Datos de la Empresa"
      >
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <strong>Denominación:</strong> {data.empresa.denominacion}
          </div>
          <div>
            <strong>Dirección:</strong> {data.empresa.direccion}
          </div>
          <div>
            <strong>Email:</strong> {data.empresa.mail}
          </div>
          <div>
            <strong>Teléfono:</strong> {data.empresa.telefono}
          </div>
          <div>
            <strong>Localidad:</strong> {data.empresa.localidad?.descripcion || 'N/A'}
          </div>
          <div>
            <strong>Tipo Responsable:</strong> {data.empresa.civa?.descripcion || 'N/A'}
          </div>
        </div>
      </Card>

      {/* Datos de la Producción */}
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
        title="Datos de la Producción"
      >
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <strong>Número de Producción:</strong> {data.produccion.numero_produccion}
          </div>
          <div>
            <strong>Fecha:</strong> {data.produccion.fecha}
          </div>
          <div>
            <strong>Hora Inicio:</strong> {data.produccion.fecha_hora_inicio}
          </div>
          <div>
            <strong>Hora Final:</strong> {data.produccion.fecha_hora_final}
          </div>
          <div>
            <strong>Demora:</strong> {data.produccion.demora_minutos} minutos
          </div>
          <div>
            <strong>Kilos Semilla:</strong> {(data.produccion.kilos_semilla || 0).toFixed(2)} kg
          </div>
          <div>
            <strong>Cliente:</strong> {data.produccion.cliente?.denominacion || 'N/A'}
          </div>
          <div>
            <strong>Productor:</strong> {data.produccion.productor?.denominacion || 'N/A'}
          </div>
          <div>
            <strong>Producto:</strong> {data.produccion.producto?.descripcion || 'N/A'}
          </div>
          <div>
            <strong>Ciclo:</strong> {data.produccion.ciclo?.descripcion || 'N/A'}
          </div>
                     <div>
             <strong>Tipo de Registración:</strong> 
             <span style={{ 
               color: '#87CEEB', 
               fontWeight: 'bold' 
             }}>
               {data.produccion.tipo_registracion === 'M' ? 'MANUAL' : data.produccion.tipo_registracion === 'A' ? 'AUTOMÁTICA' : data.produccion.tipo_registracion || 'N/A'}
             </span>
           </div>
           <div>
             <strong>Resto (+):</strong> {(data.produccion.resto_positivo || 0).toFixed(2)} kg
           </div>
           <div>
             <strong>Resto (-):</strong> {(data.produccion.resto_negativo || 0).toFixed(2)} kg
           </div>
        </div>
      </Card>

      {/* Tickets de Balanza */}
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
        title={`Tickets de Balanza (${data.tickets.cantidad_tickets})`}
      >
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            minWidth: '800px'
          }}>
                         <thead>
               <tr style={{ backgroundColor: '#f0f0f0' }}>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Nro. Ticket</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Establecimiento</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Rollo</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Fecha</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Compra/Servicio</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Nro. Remito</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>DTV</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Cliente</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Productor</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Producto</th>
                 <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Kilos Netos</th>
               </tr>
             </thead>
            <tbody>
                             {data.tickets.data.map((ticket, index) => (
                 <tr key={ticket.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.numero_ticket}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.establecimiento?.descripcion || '-'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.rollo_identificacion || '-'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.fecha_ticket}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.compra_servicio === 'C' ? 'Compra' : ticket.compra_servicio === 'S' ? 'Servicio' : ticket.compra_servicio}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.numero_remito || '-'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.dtv || '-'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                     {ticket.cliente?.denominacion || 'N/A'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                     {ticket.productor?.denominacion || 'N/A'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                     {ticket.producto?.descripcion || 'N/A'}
                   </td>
                   <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                     {(ticket.kilos_netos || 0).toFixed(2)}
                   </td>
                 </tr>
               ))}
                             <tr style={{ backgroundColor: '#e6f3ff', fontWeight: 'bold' }}>
                 <td colSpan={10} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   TOTAL KILOS NETOS:
                 </td>
                 <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   {(data.tickets.total_kilos_netos || 0).toFixed(2)}
                 </td>
               </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detalle de Fardos */}
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
        title={`Detalle de Fardos (${data.fardos.total_cantidad_fardos})`}
      >
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            minWidth: '800px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Nro. Fardo</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Fecha/Hora</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Cliente</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Producto</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Kilos Fardo</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Kilos Tara</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Peso Neto</th>
              </tr>
            </thead>
            <tbody>
              {data.fardos.data.map((fardo, index) => (
                <tr key={fardo.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {fardo.nro_fardo}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {fardo.fecha_hora}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {fardo.cliente?.denominacion || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {fardo.producto?.descripcion || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {(fardo.kilos_fardo || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {(fardo.kilos_tara || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                    {((fardo.kilos_fardo || 0) - (fardo.kilos_tara || 0)).toFixed(2)}
                  </td>
                </tr>
              ))}
                             <tr style={{ backgroundColor: '#e6f3ff', fontWeight: 'bold' }}>
                 <td colSpan={4} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   TOTAL KILOS:
                 </td>
                 <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   {(data.fardos.total_peso_fardos || 0).toFixed(2)}
                 </td>
                 <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   {(data.fardos.data.reduce((sum, fardo) => sum + (fardo.kilos_tara || 0), 0)).toFixed(2)}
                 </td>
                 <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                   {(data.fardos.data.reduce((sum, fardo) => sum + ((fardo.kilos_fardo || 0) - (fardo.kilos_tara || 0)), 0)).toFixed(2)}
                 </td>
               </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Resumen de Rindes */}
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
        title="Resumen de Rindes"
      >
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '5px',
            border: '1px solid #ddd',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>RINDE {rindes.nombreProducto?.toUpperCase()}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>
              {rindes.rindeProducto}%
            </div>
                         <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
               Sumatoria Peso Neto: {rindes.sumatoriaPesoNeto} kg
             </div>
             <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
               Con Restos: {rindes.pesoTotalConRestos} kg
             </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '5px',
            border: '1px solid #ddd',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>RINDE SEMILLA</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>
              {rindes.rindeSemilla}%
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Kilos Semilla: {(data.produccion.kilos_semilla || 0).toFixed(2)} kg
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '5px',
            border: '1px solid #ddd',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>TOTAL PROCESADO</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800' }}>
              {(data.tickets.total_kilos_netos || 0).toFixed(2)} kg
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {data.tickets.cantidad_tickets} tickets
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};

export default EmisionProduccion;
